import React, { useEffect, useState } from "react"
import { useSearchParams, Link } from "react-router-dom"
import { useDispatch } from "react-redux"
import { Container, Row, Col, Card, CardBody, Button, Badge, Alert } from "reactstrap"
import { checkPaymentFallback, markPaymentFallback } from "../../api/requests/payment-requests"
import { fetchCurrentSubscription } from "../../store/subscription/actions"

const SubscriptionPaymentSuccess = () => {
  const [searchParams] = useSearchParams()
  const dispatch = useDispatch()
  const [merchantOid, setMerchantOid] = useState("")
  const [isAlreadyUsed, setIsAlreadyUsed] = useState(false)
  const [loading, setLoading] = useState(true)
  const [isInvalid, setIsInvalid] = useState(false)
  const [paymentDetails, setPaymentDetails] = useState(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    const oid = searchParams.get("merchant_oid")
    if (oid) {
      setMerchantOid(oid)
      checkStatus(oid)
    } else {
      setLoading(false)
      setIsInvalid(true)
    }
    document.title = "Abonelik Ödemesi Başarılı - Tahsilat360"
    // PayTR callback gecikmesine karşı sigorta — 2 sn sonra bir daha tazele.
    // Genelde mount'taki dispatch yeterli; bu nadir timing race için backup.
    const retryTimer = setTimeout(() => {
      dispatch(fetchCurrentSubscription())
    }, 2000)
    return () => clearTimeout(retryTimer)
  }, [searchParams, dispatch])

  const checkStatus = async (oid) => {
    try {
      setLoading(true)
      const res = await checkPaymentFallback(oid)
      const data = await res.json()
      if (!res.ok || !data.data) { setIsInvalid(true); return }
      setPaymentDetails(data.data)
      if (data.data.fallback === true) {
        setIsAlreadyUsed(true)
      } else {
        await markPaymentFallback(oid)
        setIsAlreadyUsed(false)
      }
      // Header'daki "Deneme: X gün" / paket badge'i güncellensin diye redux store'u tazele.
      // Backend abonelik durumunu TRIAL → ACTIVE'e çekti, banner mount'tayken stale kalıyordu.
      dispatch(fetchCurrentSubscription())
    } catch {
      setIsInvalid(true)
    } finally {
      setLoading(false)
    }
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const formatPrice = (amount) => {
    if (amount == null) return "-"
    return new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount) + " ₺"
  }

  const formatDate = (d) => {
    if (!d) return new Date().toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" })
    return new Date(d).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" })
  }

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: "linear-gradient(135deg, #4CAF50 0%, #45a049 100%)" }}>
        <div className="text-center text-white">
          <div className="spinner-border mb-3" role="status" />
          <p>Ödeme durumu kontrol ediliyor...</p>
        </div>
      </div>
    )
  }

  if (isInvalid) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: "linear-gradient(135deg, #f44336 0%, #d32f2f 100%)" }}>
        <Container>
          <Row className="justify-content-center">
            <Col lg={6} md={8} sm={10}>
              <Card className="border-0 shadow-lg" style={{ borderRadius: 20 }}>
                <CardBody className="p-5 text-center">
                  <Alert color="danger" className="border-0 mb-4">
                    <h4 className="alert-heading">Hata!</h4>
                    <p className="mb-0">Geçersiz işlem numarası veya ödeme bulunamadı.</p>
                  </Alert>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    )
  }

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: "linear-gradient(135deg, #4CAF50 0%, #45a049 100%)" }}>
      <Container className="px-3 px-sm-0">
        <Row className="justify-content-center">
          <Col lg={6} md={8} sm={10}>
            <Card className="border-0 shadow-lg" style={{ borderRadius: 20, overflow: "hidden" }}>
              <CardBody className="text-center p-4 p-sm-5">
                {/* İkon */}
                <div className="d-flex align-items-center justify-content-center mb-4 mx-auto" style={{
                  width: 100, height: 100, borderRadius: "50%",
                  background: isAlreadyUsed ? "linear-gradient(135deg, #ff9800, #f57c00)" : "linear-gradient(135deg, #4CAF50, #45a049)",
                  boxShadow: isAlreadyUsed ? "0 10px 30px rgba(255,152,0,.3)" : "0 10px 30px rgba(76,175,80,.3)"
                }}>
                  <i className={`bx ${isAlreadyUsed ? "bx-info-circle" : "bx-check"} text-white`} style={{ fontSize: 48 }} />
                </div>

                <Alert color={isAlreadyUsed ? "warning" : "success"} className="border-0 mb-4">
                  <h4 className="alert-heading mb-1">{isAlreadyUsed ? "URL Daha Önce Kullanılmış!" : "Abonelik Ödemesi Başarılı!"}</h4>
                  <p className="mb-0">{isAlreadyUsed ? "Bu sayfa daha önce görüntülenmiş" : "Aboneliğiniz başarıyla aktifleştirildi"}</p>
                </Alert>

                {!isAlreadyUsed && (
                  <p className="text-muted mb-4" style={{ fontSize: 16, lineHeight: 1.6 }}>
                    {paymentDetails?.subscriptionEndDate
                      ? `Ödemeniz başarıyla alınmış ve aboneliğiniz ${formatDate(paymentDetails.subscriptionEndDate)} tarihine kadar uzatılmıştır.`
                      : "Ödemeniz başarıyla alınmış ve aboneliğiniz aktifleştirilmiştir. Tüm özellikleri kullanmaya başlayabilirsiniz!"}
                  </p>
                )}

                {/* İşlem Detayları */}
                <Card className="border-0 bg-light mb-4">
                  <CardBody className="p-4">
                    <h6 className="text-muted mb-3"><i className="bx bx-receipt me-2" />İŞLEM DETAYLARI</h6>
                    <div className="mb-3 text-center">
                      <label className="form-label text-muted small mb-1">İşlem Numarası</label>
                      <div className="d-flex align-items-center justify-content-center">
                        <Badge color={isAlreadyUsed ? "warning" : "success"} className="px-3 py-2 me-2" style={{ fontSize: 14, fontFamily: "'Courier New', monospace" }}>
                          {merchantOid}
                        </Badge>
                        <Button color={copied ? "info" : "outline-success"} size="sm" onClick={() => copyToClipboard(merchantOid)}>
                          <i className={`bx ${copied ? "bx-check-double" : "bx-copy"}`} />
                        </Button>
                      </div>
                      {copied && <small className="text-info fw-bold mt-1 d-block">Kopyalandı!</small>}
                    </div>

                    {paymentDetails && (
                      <Row className="text-center border-top py-3">
                        <Col xs={6} className="border-end">
                          <label className="form-label text-muted small mb-1 d-block">Tutar</label>
                          <div className="h5 text-success mb-0">{formatPrice(paymentDetails.amount)}</div>
                        </Col>
                        <Col xs={6}>
                          <label className="form-label text-muted small mb-1 d-block">Tarih</label>
                          <div className="text-dark" style={{ fontSize: ".9rem", paddingTop: ".4rem" }}>{formatDate(paymentDetails.paymentDate)}</div>
                        </Col>
                      </Row>
                    )}
                  </CardBody>
                </Card>

                <Badge color={isAlreadyUsed ? "warning" : "success"} className="px-4 py-3 mb-4" style={{ fontSize: 16, fontWeight: 600, borderRadius: 25 }}>
                  <i className={`bx ${isAlreadyUsed ? "bx-info-circle" : "bx-check-circle"} me-2`} />
                  {isAlreadyUsed
                    ? "URL Daha Önce Kullanılmış"
                    : paymentDetails?.subscriptionEndDate
                    ? "Abonelik Uzatıldı"
                    : "Abonelik Aktifleştirildi"}
                </Badge>

                {!isAlreadyUsed && paymentDetails?.subscriptionEndDate && (
                  <div className="mb-3 p-3 text-center" style={{
                    backgroundColor: "#F0FAE5", borderRadius: 12, border: "1px solid #71C30B40"
                  }}>
                    <i className="bx bx-calendar-check me-2" style={{ color: "#71C30B" }} />
                    <span style={{ fontSize: ".9rem" }}>
                      Aboneliğiniz <strong>{formatDate(paymentDetails.subscriptionEndDate)}</strong> tarihine kadar uzatılmıştır.
                    </span>
                  </div>
                )}

                <div className="text-center mb-4">
                  <Link to="/dashboard">
                    <Button color="primary" className="w-100 w-sm-auto px-4 py-2" style={{ fontWeight: 600 }}>
                      <i className="bx bx-home me-2" />Kontrol Paneline Git
                    </Button>
                  </Link>
                </div>

                <div className="mt-4 pt-4 border-top">
                  <p className="text-muted small mb-0">
                    <i className="bx bx-shield-check me-2" />Bu işlem güvenli SSL bağlantısı ile gerçekleştirilmiştir
                  </p>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default SubscriptionPaymentSuccess
